import React, {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ClientUser } from '@tv2/lib-api/src/ClientUser';
import { useUserInteractionTrack } from '../../hooks/tracking/useUserInteractionTrack';
import { clientSideCookies } from '../../libs/clientSideCookies';
import * as authService from './auth';
import { getCookie } from '../../libs/cookieParser';
import { LoginTracking } from '../../types/LoginTracking';

export type UserContextType = {
  completed: boolean;
  user: ClientUser | null;
  accessToken: string | null;
  ssrLoggedInHint: boolean;
};

export const UserContext = createContext<UserContextType | null>(null);

const UpdateUserContext = createContext<{
  updateAccessToken: () => Promise<void>;
  logout: () => void;
} | null>(null);

export const UserProvider: FunctionComponent<PropsWithChildren<{ ssrLoggedInHint: boolean }>> = ({
  children,
  ssrLoggedInHint,
}) => {
  const [user, setUser] = useState<ClientUser | null>(null);
  const [completed, setCompleted] = useState<boolean>(false);
  const trackUserInteraction = useUserInteractionTrack();

  const userFetcher = useCallback(async () => {
    const data = await authService.fetchUser();
    const trackLogin = getCookie('track_login');
    if (data?.user) setUser(data?.user);
    if (trackLogin) {
      if (trackLogin === LoginTracking.successfulLogin) {
        trackUserInteraction({ data: { label: 'tv2.login.successful login' } });
      } else if (trackLogin === LoginTracking.failedLogin) {
        trackUserInteraction({ data: { label: 'tv2.login.failed login' } });
      } else if (trackLogin === LoginTracking.successfulLogout) {
        trackUserInteraction({ data: { label: 'tv2.login.successful logout' } });
      } else if (trackLogin === LoginTracking.failedLogout) {
        trackUserInteraction({ data: { label: 'tv2.login.failed logout' } });
      }
      clientSideCookies.setCookie({
        key: 'track_login',
        value: LoginTracking.none,
        domain: '',
        path: '/',
        maxAge: LoginTracking.maxAge,
        secure: true,
      });
    }
    setCompleted(true);
    return data;
  }, [trackUserInteraction]);

  useEffect(() => {
    userFetcher();
  }, [userFetcher]);

  const updateAccessToken = useCallback(async () => {
    userFetcher();
  }, [userFetcher]);

  const val = useMemo(
    () => ({
      completed,
      user,
      accessToken: user?.accessToken ?? null,
      ssrLoggedInHint,
    }),
    [completed, user, ssrLoggedInHint]
  );

  const fns = useMemo(
    () => ({
      updateAccessToken,
      logout: authService.logout,
    }),
    [updateAccessToken]
  );

  return (
    <UpdateUserContext.Provider value={fns}>
      <UserContext.Provider value={val}>{children}</UserContext.Provider>
    </UpdateUserContext.Provider>
  );
};

function useAssertedUserContext() {
  const val = useContext(UserContext);
  if (val === null) throw new Error('missing user provider');
  return val;
}

export function useUpdateUser() {
  const val = useContext(UpdateUserContext);
  if (val === null) throw new Error('missing user provider');
  return val;
}

export function useUser() {
  const user = useAssertedUserContext();

  if (!user) throw new Error('missing user context');

  return user;
}

export function useIsTv2User(user: ClientUser | null) {
  return user?.email?.endsWith('@tv2.dk') ?? false;
}

export function useIsLoggedIn() {
  return useLoginStatus() === 'LOGGED_IN';
}
export function useLoginStatus(): 'SSR_UNKNOWN' | 'LOGGED_IN' | 'NOT_LOGGED_IN' {
  const { accessToken, completed } = useContext(UserContext) ?? {};
  switch (true) {
    case Boolean(completed && accessToken):
      return 'LOGGED_IN';
    case Boolean(completed && !accessToken):
      return 'NOT_LOGGED_IN';
    default:
      return 'SSR_UNKNOWN';
  }
}
